<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

import L from "leaflet";
import "leaflet/dist/leaflet.css";

const props = withDefaults(
  defineProps<{
    lat: number | null;
    lng: number | null;
    label?: string;
    zoom?: number;
  }>(),
  { label: "", zoom: 15 },
);

const mapEl = ref<HTMLElement | null>(null);
let map: L.Map | null = null;
let marker: L.Marker | null = null;

function pin(): L.DivIcon {
  return L.divIcon({
    className: "",
    html: '<span class="loc-pin"></span>',
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

function render(): void {
  if (!map) return;
  if (props.lat == null || props.lng == null) {
    marker?.remove();
    marker = null;
    return;
  }
  const at: L.LatLngExpression = [props.lat, props.lng];
  map.setView(at, props.zoom);
  if (marker) marker.setLatLng(at);
  else marker = L.marker(at, { icon: pin() }).addTo(map);
  if (props.label) marker.bindPopup(props.label);
  setTimeout(() => map?.invalidateSize(), 0);
}

function initMap(): void {
  if (map || !mapEl.value) return;
  map = L.map(mapEl.value, { attributionControl: true }).setView(
    [54.5, -3.5],
    5,
  ); // UK fallback until we have a point
  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    attribution: "&copy; OpenStreetMap",
    maxZoom: 19,
  }).addTo(map);
  render();
}

onMounted(() => initMap());
watch(
  () => [props.lat, props.lng, props.label],
  async () => {
    await nextTick();
    initMap();
    render();
  },
);
onBeforeUnmount(() => {
  map?.remove();
  map = null;
});
</script>

<template>
  <div
    ref="mapEl"
    class="h-56 w-full overflow-hidden rounded-lg border border-neutral-200 bg-neutral-100"
  />
</template>

<style>
.loc-pin {
  display: block;
  width: 18px;
  height: 18px;
  border-radius: 999px 999px 999px 0;
  transform: rotate(-45deg);
  background: var(--brand-primary, #ad2d1d);
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
}
</style>
